<template>
	<v-container fluid class="pa-4 dms-component">
		<!-- Toolbar -->
		<v-row no-gutters class="mb-3 align-center">
			<v-col cols="12" md="6">
				<!-- Breadcrumb -->
				<v-breadcrumbs :items="breadcrumbItems" class="pa-0">
					<template v-slot:item="{ item }">
						<v-breadcrumbs-item
							:disabled="item.disabled"
							@click="navigateToFolder(item.folderId)"
							class="cursor-pointer"
						>
							{{ item.text }}
						</v-breadcrumbs-item>
					</template>
				</v-breadcrumbs>
			</v-col>
			<v-col cols="12" md="6" class="d-flex justify-end align-center">
				<v-text-field
					v-model="search"
					prepend-icon="mdi-magnify"
					label="Cerca documenti..."
					dense
					outlined
					hide-details
					clearable
					class="mr-2 flex-grow-1"
					style="min-width: 150px"
				></v-text-field>
				<v-btn-toggle v-model="viewMode" mandatory dense class="mr-2">
					<v-btn small value="grid"><v-icon small>mdi-view-grid</v-icon></v-btn>
					<v-btn small value="list"><v-icon small>mdi-view-list</v-icon></v-btn>
				</v-btn-toggle>
				<v-btn v-if="canUpload" color="primary" small @click="newFolderDialog = true" class="mr-2">
					<v-icon left small class="d-none d-sm-inline">mdi-folder-plus</v-icon>
					Nuova Cartella
				</v-btn>
				<v-btn v-if="canUpload" color="success" small class="upload-btn">
					<v-icon left small class="d-none d-sm-inline">mdi-upload</v-icon>
					Carica
					<input
						ref="fileInput"
						type="file"
						multiple
						class="upload-btn-input"
						@change="handleFileUpload"
					/>
				</v-btn>
				<v-btn v-if="canUpload" color="success" small outlined class="upload-btn ml-2">
					<v-icon left small class="d-none d-sm-inline">mdi-folder-upload</v-icon>
					Carica cartella
					<input
						ref="folderInput"
						type="file"
						webkitdirectory
						multiple
						class="upload-btn-input"
						@change="handleFolderUpload"
					/>
				</v-btn>
			</v-col>
		</v-row>

		<!-- Search scope toggle -->
		<v-row v-if="search" no-gutters class="mb-2 align-center">
			<v-chip-group v-model="searchScope" mandatory>
				<v-chip small filter value="local">Cartella corrente</v-chip>
				<v-chip small filter value="global">Ovunque</v-chip>
			</v-chip-group>
		</v-row>

		<!-- Area filter + storage -->
		<v-row no-gutters class="mb-2 align-center" style="min-height: 40px">
			<v-col v-if="!currentFolderId">
				<v-chip-group>
					<v-chip :outlined="areaFilter !== 'all'" small @click="areaFilter = 'all'">Tutti</v-chip>
					<v-chip :outlined="areaFilter !== 'tenant'" small @click="areaFilter = 'tenant'" color="blue" :text-color="areaFilter === 'tenant' ? 'white' : 'blue'">
						<v-icon left x-small>mdi-office-building</v-icon>Condivisi
					</v-chip>
					<v-chip :outlined="areaFilter !== 'client'" small @click="areaFilter = 'client'" color="green" :text-color="areaFilter === 'client' ? 'white' : 'green'">
						<v-icon left x-small>mdi-account</v-icon>I miei
					</v-chip>
				</v-chip-group>
			</v-col>
			<v-spacer></v-spacer>
			<v-col cols="auto" class="text-caption grey--text">
				<v-icon x-small class="mr-1">mdi-harddisk</v-icon>{{ formatSize(filteredStorageSize) }}<span v-if="storage.quota"> / {{ storage.quota }} MB</span>
			</v-col>
		</v-row>

		<!-- Bulk toolbar -->
		<v-card v-if="hasSelection" outlined class="mb-3 bulk-toolbar">
			<v-card-text class="d-flex align-center py-2">
				<v-icon class="mr-2" color="primary">mdi-checkbox-multiple-marked</v-icon>
				<span class="text-body-2">{{ selectionCount }} elemento/i selezionato/i</span>
				<v-spacer></v-spacer>
				<v-btn small text @click="openBulkMoveDialog"><v-icon left small>mdi-folder-move</v-icon>Sposta</v-btn>
				<v-btn small text @click="bulkDownload" :loading="bulkLoading"><v-icon left small>mdi-download</v-icon>Scarica ZIP</v-btn>
				<v-btn small text color="error" @click="bulkDeleteDialog = true"><v-icon left small>mdi-delete</v-icon>Elimina</v-btn>
				<v-btn small icon @click="clearSelection"><v-icon>mdi-close</v-icon></v-btn>
			</v-card-text>
		</v-card>

		<!-- Drop zone (tutta pagina) -->
		<div
			ref="dropZone"
			class="drop-zone"
			:class="{ 'drop-zone-active': isDragging && canUpload, 'marquee-active': marquee.active }"
			style="max-height: 500px; overflow-y: auto; position: relative"
			@dragenter.prevent="canUpload ? onDragEnter() : null"
			@dragover.prevent
			@dragleave.prevent="canUpload ? onDragLeave() : null"
			@drop.prevent="canUpload ? handleDrop($event) : null"
			@mousedown.left="onMarqueeMouseDown"
		>
			<!-- Marquee overlay -->
			<div v-if="marquee.active" class="marquee-rect" :style="{
				left: marquee.x + 'px',
				top: marquee.y + 'px',
				width: marquee.w + 'px',
				height: marquee.h + 'px'
			}"></div>
			<!-- Upload progress -->
			<v-progress-linear
				v-if="uploading"
				:value="uploadProgress"
				color="primary"
				class="mb-3"
				height="6"
				rounded
			></v-progress-linear>

			<!-- Cartelle -->
			<div v-if="filteredFolders.length && !isGlobalSearch" class="mb-4">
				<div class="text-overline grey--text mb-2">Cartelle</div>
				<!-- Vista griglia -->
				<v-row v-if="viewMode === 'grid'" dense>
					<v-col v-for="folder in filteredFolders" :key="'f-' + folder.id" cols="6" sm="4" md="3" lg="2">
						<v-card
							outlined
							class="text-center pa-3 cursor-pointer hover-card d-flex flex-column align-center justify-center"
							style="height: 140px"
							:class="{ 'dragging': dragItem && dragItem.type === 'folder' && dragItem.id === folder.id, 'drag-over': dragOverFolder === folder.id, 'selected-multi': selectedFolderIds.includes(folder.id) }"
							:data-folder-id="folder.id"
							draggable="true"
							@click="navigateToFolder(folder.id)"
							@contextmenu.prevent="showFolderMenu($event, folder)"
							@dragstart="onItemDragStart($event, 'folder', folder)"
							@dragend="onItemDragEnd"
							@dragover.prevent.stop="onFolderDragOver($event, folder)"
							@dragleave.prevent.stop="onFolderDragLeave"
							@drop.prevent.stop="onFolderDrop($event, folder)"
						>
							<v-icon size="48" color="amber darken-2">mdi-folder</v-icon>
							<div class="text-body-2 mt-1 d-flex align-center justify-center" style="max-width: 100%">
								<span class="text-truncate" style="flex: 1; min-width: 0">{{ folder.name }}</span>
								<v-tooltip bottom v-if="folder.is_portal_shared">
									<template v-slot:activator="{ on }">
										<v-icon v-on="on" x-small color="teal" class="ml-1 flex-shrink-0">mdi-account-multiple</v-icon>
									</template>
									<span>Condiviso con te</span>
								</v-tooltip>
							</div>
							<v-chip v-if="folder.area === 'tenant'" x-small color="blue" text-color="white" class="mt-1">Condivisa</v-chip>
							<div class="text-caption grey--text">{{ folder.documents_count || 0 }} file<span v-if="folder.documents_sum_size"> · {{ formatSize(folder.documents_sum_size) }}</span></div>
						</v-card>
					</v-col>
				</v-row>
				<!-- Vista lista -->
				<v-simple-table v-else dense class="bordered-table">
					<tbody>
						<tr
							v-for="folder in filteredFolders"
							:key="'f-' + folder.id"
							class="cursor-pointer"
							:class="{ 'dragging': dragItem && dragItem.type === 'folder' && dragItem.id === folder.id, 'drag-over': dragOverFolder === folder.id }"
							draggable="true"
							@click="navigateToFolder(folder.id)"
							@contextmenu.prevent="showFolderMenu($event, folder)"
							@dragstart="onItemDragStart($event, 'folder', folder)"
							@dragend="onItemDragEnd"
							@dragover.prevent.stop="onFolderDragOver($event, folder)"
							@dragleave.prevent.stop="onFolderDragLeave"
							@drop.prevent.stop="onFolderDrop($event, folder)"
						>
							<td style="width: 40px"><v-icon color="amber darken-2">mdi-folder</v-icon></td>
							<td>
								{{ folder.name }}
								<v-tooltip bottom v-if="folder.is_portal_shared">
									<template v-slot:activator="{ on }">
										<v-icon v-on="on" x-small color="teal" class="ml-1">mdi-account-multiple</v-icon>
									</template>
									<span>Condiviso con te</span>
								</v-tooltip>
							</td>
							<td class="text-right">
								<v-chip v-if="folder.area === 'tenant'" x-small color="blue" text-color="white">Condivisa</v-chip>
								<v-chip v-else x-small color="green" text-color="white">Mia</v-chip>
							</td>
						</tr>
					</tbody>
				</v-simple-table>
			</div>

			<!-- Documenti -->
			<div v-if="filteredDocuments.length">
				<div class="text-overline grey--text mb-2">Documenti</div>
				<!-- Vista griglia -->
				<v-row v-if="viewMode === 'grid'" dense>
					<v-col v-for="doc in filteredDocuments" :key="'d-' + doc.id" cols="6" sm="4" md="3" lg="2">
						<v-card
							outlined
							class="dms-doc-card"
							:class="{ 'dragging': dragItem && dragItem.type === 'doc' && dragItem.id === doc.id, 'selected': selectedDoc && selectedDoc.id === doc.id, 'selected-multi': selectedDocIds.includes(doc.id) }"
							:data-doc-id="doc.id"
							draggable="true"
							@dragstart="onItemDragStart($event, 'doc', doc)"
							@dragend="onItemDragEnd"
							@click="toggleSelectDocument(doc)"
							@dblclick.stop="isPreviewable(doc) ? openPreview(doc) : downloadDoc(doc)"
							@contextmenu.prevent="showDocMenu($event, doc)"
						>
							<div class="dms-doc-preview">
								<img v-if="doc.thumbnail" :src="'data:image/png;base64,' + doc.thumbnail" />
								<v-icon v-else size="64" :color="getFileColor(doc.ext)">{{ getFileIcon(doc.ext) }}</v-icon>
								<v-tooltip bottom v-if="doc.is_shared">
									<template v-slot:activator="{ on }">
										<v-icon v-on="on" x-small color="primary" style="position: absolute; top: 8px; right: 8px">mdi-share-variant</v-icon>
									</template>
									<span>Condiviso dalla libreria</span>
								</v-tooltip>
								<v-tooltip bottom v-if="doc.is_portal_shared">
									<template v-slot:activator="{ on }">
										<v-icon v-on="on" x-small color="teal" style="position: absolute; bottom: 8px; right: 8px">mdi-account-multiple</v-icon>
									</template>
									<span>Condiviso con te</span>
								</v-tooltip>
								<v-tooltip bottom v-if="doc.expires_at">
									<template v-slot:activator="{ on }">
										<v-icon v-on="on" x-small color="orange" style="position: absolute; top: 8px; left: 8px">mdi-calendar-alert</v-icon>
									</template>
									<span>Scadenza: {{ formatDateShort(doc.expires_at) }}</span>
								</v-tooltip>
								<div class="dms-doc-tags" v-if="doc.tags && doc.tags.length">
									<v-chip v-for="tag in doc.tags.slice(0, 2)" :key="tag.id" x-small :color="tag.color || 'grey'" text-color="white">{{ tag.name }}</v-chip>
									<v-chip v-if="doc.tags.length > 2" x-small color="grey" text-color="white">+{{ doc.tags.length - 2 }}</v-chip>
								</div>
							</div>
							<div class="dms-doc-info">
								<v-icon size="18" :color="getFileColor(doc.ext)">{{ getFileIcon(doc.ext) }}</v-icon>
								<span class="text-body-2 text-truncate" style="flex: 1; min-width: 0">{{ doc.name | fileName }}.{{ doc.ext }}</span>
								<v-tooltip bottom>
									<template v-slot:activator="{ on }">
										<v-icon v-on="on" x-small :color="doc.area === 'tenant' ? 'blue' : 'green'" class="flex-shrink-0">mdi-circle</v-icon>
									</template>
									<span>{{ doc.area === 'tenant' ? "Caricato dall'azienda" : 'Caricato da te' }}</span>
								</v-tooltip>
								<v-btn icon x-small @click.stop="showDocMenu($event, doc)"><v-icon>mdi-dots-vertical</v-icon></v-btn>
							</div>
						</v-card>
					</v-col>
				</v-row>
				<!-- Vista lista -->
				<v-simple-table v-else dense class="bordered-table">
					<thead>
						<tr>
							<th style="width: 40px"></th>
							<th>Nome</th>
							<th v-if="isGlobalSearch" class="d-none d-sm-table-cell">Cartella</th>
							<th class="d-none d-sm-table-cell">Dimensione</th>
							<th class="d-none d-sm-table-cell">Data</th>
							<th class="d-none d-md-table-cell">Tag</th>
							<th style="width: 60px"></th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="doc in filteredDocuments"
							:key="'d-' + doc.id"
							class="cursor-pointer"
							:class="{ 'dragging': dragItem && dragItem.type === 'doc' && dragItem.id === doc.id }"
							draggable="true"
							@click="debouncedOpenDocDetails(doc)"
							@dblclick.stop="cancelDebouncedClick(); isPreviewable(doc) ? openPreview(doc) : downloadDoc(doc)"
							@contextmenu.prevent="showDocMenu($event, doc)"
							@dragstart="onItemDragStart($event, 'doc', doc)"
							@dragend="onItemDragEnd"
						>
							<td><v-icon :color="getFileColor(doc.ext)">{{ getFileIcon(doc.ext) }}</v-icon></td>
							<td>
								{{ doc.name | fileName(doc.ext) }}
								<v-tooltip bottom v-if="doc.is_shared">
									<template v-slot:activator="{ on }">
										<v-icon v-on="on" x-small color="primary" class="ml-1">mdi-share-variant</v-icon>
									</template>
									<span>Condiviso dalla libreria</span>
								</v-tooltip>
								<v-tooltip bottom v-if="doc.is_portal_shared">
									<template v-slot:activator="{ on }">
										<v-icon v-on="on" x-small color="teal" class="ml-1">mdi-account-multiple</v-icon>
									</template>
									<span>Condiviso con te</span>
								</v-tooltip>
								<v-tooltip bottom v-if="doc.expires_at">
									<template v-slot:activator="{ on }">
										<v-icon v-on="on" x-small color="orange" class="ml-1">mdi-calendar-alert</v-icon>
									</template>
									<span>Scadenza: {{ formatDateShort(doc.expires_at) }}</span>
								</v-tooltip>
							</td>
							<td v-if="isGlobalSearch" class="d-none d-sm-table-cell text-no-wrap text-caption">
								<v-icon x-small class="mr-1">mdi-folder</v-icon>{{ doc.folder ? doc.folder.name : 'Home' }}
							</td>
							<td class="d-none d-sm-table-cell text-no-wrap">{{ formatSize(doc.size) }}</td>
							<td class="d-none d-sm-table-cell text-no-wrap">{{ doc.created_at | date }}</td>
							<td class="d-none d-md-table-cell">
								<v-chip v-for="tag in (doc.tags || []).slice(0, 3)" :key="tag.id" x-small :color="tag.color || 'grey'" text-color="white" class="mr-1">
									{{ tag.name }}
								</v-chip>
								<span v-if="(doc.tags || []).length > 3" class="text-caption grey--text">+{{ doc.tags.length - 3 }}</span>
							</td>
							<td>
								<v-btn icon x-small @click.stop="downloadDoc(doc)"><v-icon small>mdi-download</v-icon></v-btn>
							</td>
						</tr>
					</tbody>
				</v-simple-table>
			</div>

			<!-- Empty state -->
			<div v-if="!loading && !filteredFolders.length && !filteredDocuments.length" class="empty-state text-center">
				<v-icon size="64" color="grey lighten-1">mdi-cloud-upload-outline</v-icon>
				<div class="text-h6 grey--text mt-3">Nessun contenuto</div>
				<div class="text-body-2 grey--text">Trascina file qui o usa il pulsante Carica</div>
			</div>

			<!-- Loading -->
			<v-progress-circular v-if="loading" indeterminate color="primary" class="d-block mx-auto my-12"></v-progress-circular>

			<!-- Drag overlay -->
			<div v-if="isDragging" class="drop-overlay">
				<v-icon size="64" color="primary">mdi-cloud-upload</v-icon>
				<div class="text-h6 primary--text mt-2">Rilascia i file qui</div>
			</div>
		</div>


		<!-- Context menu documenti -->
		<v-menu v-model="contextMenu.show" :position-x="contextMenu.x" :position-y="contextMenu.y" absolute content-class="round-card">
			<v-list dense>
				<v-list-item @click="openPreview(contextMenu.item)" v-if="contextMenu.type === 'doc' && isPreviewable(contextMenu.item)">
					<v-list-item-icon><v-icon small>mdi-eye</v-icon></v-list-item-icon>
					<v-list-item-title>Anteprima</v-list-item-title>
				</v-list-item>
				<v-list-item @click="openDocDetails(contextMenu.item)" v-if="contextMenu.type === 'doc'">
					<v-list-item-icon><v-icon small>mdi-information-outline</v-icon></v-list-item-icon>
					<v-list-item-title>Dettagli</v-list-item-title>
				</v-list-item>
				<v-list-item @click="downloadDoc(contextMenu.item)" v-if="contextMenu.type === 'doc'">
					<v-list-item-icon><v-icon small>mdi-download</v-icon></v-list-item-icon>
					<v-list-item-title>Scarica</v-list-item-title>
				</v-list-item>
				<v-list-item @click="downloadFolder(contextMenu.item)" v-if="contextMenu.type === 'folder'">
					<v-list-item-icon><v-icon small>mdi-folder-download</v-icon></v-list-item-icon>
					<v-list-item-title>Scarica cartella</v-list-item-title>
				</v-list-item>
				<v-list-item @click="openRenameDialog()" v-if="canModify(contextMenu.item)">
					<v-list-item-icon><v-icon small>mdi-pencil</v-icon></v-list-item-icon>
					<v-list-item-title>Rinomina</v-list-item-title>
				</v-list-item>
				<v-list-item @click="openMoveDialog()" v-if="canModify(contextMenu.item)">
					<v-list-item-icon><v-icon small>mdi-folder-move</v-icon></v-list-item-icon>
					<v-list-item-title>Sposta</v-list-item-title>
				</v-list-item>
				<v-list-item @click="openTagDialog()" v-if="contextMenu.type === 'doc'">
					<v-list-item-icon><v-icon small>mdi-tag</v-icon></v-list-item-icon>
					<v-list-item-title>Tag</v-list-item-title>
				</v-list-item>
				<v-list-item @click="confirmDelete()" v-if="canModify(contextMenu.item)">
					<v-list-item-icon><v-icon small color="red">mdi-delete</v-icon></v-list-item-icon>
					<v-list-item-title class="red--text">Elimina</v-list-item-title>
				</v-list-item>
			</v-list>
		</v-menu>

		<!-- Dettagli documento (sidebar) -->
		<v-navigation-drawer
			v-model="detailDrawer"
			right
			app
			clipped
			temporary
			width="380"
		>
			<template v-if="selectedDoc">
				<v-card flat>
					<v-card-title class="d-flex align-center" style="flex-wrap: nowrap">
						<v-icon left :color="getFileColor(selectedDoc.ext)" class="flex-shrink-0">{{ getFileIcon(selectedDoc.ext) }}</v-icon>
						<span class="text-subtitle-1 text-truncate" style="min-width: 0; flex: 1">{{ selectedDoc.name | fileName(selectedDoc.ext) }}</span>
						<v-btn icon small class="flex-shrink-0 ml-auto" @click="detailDrawer = false"><v-icon>mdi-close</v-icon></v-btn>
					</v-card-title>
					<v-divider></v-divider>
					<v-card-text>
						<div class="mb-3">
							<div class="text-caption grey--text">Nome file</div>
							<div>{{ selectedDoc.name | fileName(selectedDoc.ext) }}</div>
						</div>
						<div class="mb-3">
							<div class="text-caption grey--text">Dimensione</div>
							<div>{{ formatSize(selectedDoc.size) }}</div>
						</div>
						<div class="mb-3">
							<div class="text-caption grey--text">Data caricamento</div>
							<div>{{ selectedDoc.created_at | date }}</div>
						</div>
						<div class="mb-3" v-if="selectedDoc.description">
							<div class="text-caption grey--text">Descrizione</div>
							<div>{{ selectedDoc.description }}</div>
						</div>
						<div class="mb-3">
							<div class="text-caption grey--text">Versione</div>
							<div>{{ selectedDoc.version }}</div>
						</div>
						<div class="mb-3">
							<div class="text-caption grey--text">Area</div>
							<v-chip x-small :color="selectedDoc.area === 'tenant' ? 'blue' : 'green'" text-color="white">
								{{ selectedDoc.area === 'tenant' ? 'Condiviso' : 'Mio' }}
							</v-chip>
						</div>
						<div class="mb-3" v-if="selectedDoc.expires_at">
							<div class="text-caption grey--text">Scadenza</div>
							<v-chip small color="orange" text-color="white">
								<v-icon left x-small>mdi-calendar-alert</v-icon>
								{{ formatDateShort(selectedDoc.expires_at) }}
							</v-chip>
						</div>
						<div class="mb-3" v-if="selectedDoc.tags && selectedDoc.tags.length">
							<div class="text-caption grey--text">Tag</div>
							<v-chip v-for="tag in selectedDoc.tags" :key="tag.id" small :color="tag.color || 'grey'" text-color="white" class="mr-1 mt-1">
								{{ tag.name }}
							</v-chip>
						</div>

						<!-- Versioni -->
						<div class="mb-3" v-if="docVersions.length">
							<div class="text-caption grey--text mb-1">Versioni precedenti</div>
							<v-list dense class="pa-0">
								<v-list-item v-for="ver in docVersions" :key="ver.id" dense>
									<v-list-item-content>
										<v-list-item-title class="text-body-2">v{{ ver.version }} - {{ ver.original_name }}</v-list-item-title>
										<v-list-item-subtitle>{{ ver.created_at | date }} - {{ formatSize(ver.size) }}</v-list-item-subtitle>
									</v-list-item-content>
								</v-list-item>
							</v-list>
						</div>
					</v-card-text>
					<v-card-actions>
						<v-btn color="primary" outlined @click="downloadDoc(selectedDoc)">
							<v-icon left small>mdi-download</v-icon>Scarica
						</v-btn>
						<v-btn color="success" text @click="$refs.versionInput.click()" v-if="canModify(selectedDoc)">
							<v-icon left small>mdi-upload</v-icon>Nuova Versione
						</v-btn>
						<input
							ref="versionInput"
							type="file"
							style="position: absolute; width: 0; height: 0; overflow: hidden; opacity: 0"
							@change="handleVersionUpload"
						/>
					</v-card-actions>
				</v-card>
			</template>
		</v-navigation-drawer>

		<!-- Dialog nuova cartella -->
		<v-dialog v-model="newFolderDialog" max-width="400">
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3"><p class="pl-2 pl-xl-3">Nuova Cartella</p><v-spacer></v-spacer></v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					<v-text-field
						v-model="newFolderName"
						label="Nome cartella"
						outlined
						dense
						autofocus
						@keyup.enter="createFolder"
					></v-text-field>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="newFolderDialog = false">Annulla</v-btn>
					<v-btn text color="info" @click="createFolder" :loading="saving">Crea</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog rinomina -->
		<v-dialog v-model="renameDialog" max-width="400">
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3"><p class="pl-2 pl-xl-3">Rinomina</p><v-spacer></v-spacer></v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					<v-text-field
						v-model="renameName"
						label="Nuovo nome"
						outlined
						dense
						autofocus
						@keyup.enter="renameItem"
					></v-text-field>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="renameDialog = false">Annulla</v-btn>
					<v-btn text color="info" @click="renameItem" :loading="saving">Salva</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog tag -->
		<v-dialog v-model="tagDialog" max-width="500">
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3"><p class="pl-2 pl-xl-3">Gestisci Tag</p><v-spacer></v-spacer></v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					<v-combobox
						ref="tagCombobox"
						v-model="selectedTags"
						:items="allTags"
						item-text="name"
						item-value="id"
						label="Tag"
						multiple
						chips
						small-chips
						deletable-chips
						outlined
						dense
					></v-combobox>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="tagDialog = false">Annulla</v-btn>
					<v-btn text color="info" @click="saveTags" :loading="saving">Salva</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog sposta -->
		<v-dialog v-model="moveDialog" max-width="500">
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3"><p class="pl-2 pl-xl-3">Sposta in...</p><v-spacer></v-spacer></v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					<v-list dense class="pa-0">
						<v-list-item @click="moveFolderTarget = null" :class="{ 'v-list-item--active primary--text': moveFolderTarget === null }">
							<v-list-item-icon class="mr-2 pr-2"><v-icon color="amber darken-2">mdi-folder-home</v-icon></v-list-item-icon>
							<v-list-item-title>Home</v-list-item-title>
						</v-list-item>
					</v-list>
					<v-treeview
						:items="folderTree"
						item-key="id"
						item-text="name"
						activatable
						@update:active="moveFolderTarget = $event[0] || null"
						open-on-click
						dense
					>
						<template v-slot:prepend="{}">
							<v-icon color="amber darken-2">mdi-folder</v-icon>
						</template>
					</v-treeview>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="moveDialog = false">Annulla</v-btn>
					<v-btn text color="info" @click="moveItem" :loading="saving">Sposta</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog conferma eliminazione -->
		<v-dialog v-model="deleteDialog" max-width="400">
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3"><p class="pl-2 pl-xl-3">Conferma Eliminazione</p><v-spacer></v-spacer></v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					Sei sicuro di voler eliminare <strong>{{ contextMenu.item ? contextMenu.item.name : '' }}</strong>?
					<span v-if="contextMenu.type === 'folder'">Tutti i contenuti della cartella verranno eliminati.</span>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="deleteDialog = false">Annulla</v-btn>
					<v-btn text color="error" @click="deleteItem" :loading="saving">Elimina</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<!-- Dialog: bulk move -->
		<v-dialog v-model="bulkMoveDialog" max-width="500" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Sposta {{ selectionCount }} elemento/i</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					<p class="mb-2">Scegli la cartella di destinazione.</p>
					<v-select
						v-model="bulkMoveFolderTarget"
						:items="[{ id: null, name: '— Radice —' }].concat(folders)"
						item-text="name"
						item-value="id"
						label="Cartella destinazione"
						:menu-props="{'offset-y': true}"
						outlined dense clearable
					></v-select>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="bulkMoveDialog = false">Annulla</v-btn>
					<v-btn text color="info" @click="confirmBulkMove" :loading="bulkLoading">Sposta</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog: bulk delete -->
		<v-dialog v-model="bulkDeleteDialog" max-width="450" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Conferma eliminazione</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					Eliminare <strong>{{ selectedDocIds.length }}</strong> documento/i e <strong>{{ selectedFolderIds.length }}</strong> cartella/e?
					<span v-if="selectedFolderIds.length">
						<br><br><v-icon small color="warning">mdi-alert</v-icon>
						Le cartelle saranno eliminate con tutto il loro contenuto.
					</span>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="bulkDeleteDialog = false">Annulla</v-btn>
					<v-btn text color="error" @click="confirmBulkDelete" :loading="bulkLoading">Elimina</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<file-preview-dialog
			v-model="previewDialog"
			:doc="previewDoc"
			:url="previewUrl"
		/>
	</v-container>
</template>

<script>
import DmsMixin from '@/mixins/DmsMixin'
import FilePreviewDialog from '@/components/common/FilePreviewDialog'

export default {
	name: 'PortalDocuments',
	mixins: [DmsMixin],
	components: { FilePreviewDialog },
	data() {
		return {
			areaFilter: 'all'
		}
	},
	computed: {
		canUpload() {
			// Check globale da branding
			try {
				var branding = JSON.parse(localStorage.getItem('portal-branding') || '{}')
				if (branding.portal_client_upload_enabled === false || branding.portal_client_upload_enabled === 'false' || branding.portal_client_upload_enabled === '0') return false
			} catch (e) {}
			// Check per-utente dalla company corrente
			var company = this.$store.getters.portalCurrentCompany
			if (company && (company.can_upload === false || company.can_upload === 0)) return false
			return true
		},
		filteredFolders() {
			var items = this.folders
			if (this.search) {
				var s = this.search.toLowerCase()
				items = items.filter(f => f.name.toLowerCase().includes(s))
			}
			if (this.areaFilter !== 'all') {
				items = items.filter(f => f.area === this.areaFilter)
			}
			return items
		},
		filteredDocuments() {
			if (this.search && this.searchScope === 'global') {
				return this.searchResults
			}
			var items = this.documents
			if (this.search) {
				var s = this.search.toLowerCase()
				items = items.filter(d =>
					d.name.toLowerCase().includes(s) ||
					(d.tags && d.tags.some(t => t.name.toLowerCase().includes(s)))
				)
			}
			if (this.areaFilter !== 'all') {
				items = items.filter(d => d.area === this.areaFilter)
			}
			return items
		},
		filteredStorageSize() {
			if (this.areaFilter === 'tenant') return this.storage.tenant
			if (this.areaFilter === 'client') return this.storage.client
			return this.storage.total
		}
	},
	watch: {
		'$route.params.folderId': {
			immediate: true,
			handler(val) {
				this.currentFolderId = val || null
				this.loadContent()
			}
		}
	},
	methods: {
		dmsConfig() {
			return {
				apiBase: 'portal',
				breadcrumbRootLabel: 'Documenti',
				viewModeStorageKey: 'portal-view-mode',
				defaultViewMode: 'grid',
				hasRecentActivity: false
			}
		},
		loadContent() {
			this.loading = true
			this.axios.get(this.apiBase + '/folders', {
				params: { parent_id: this.currentFolderId }
			}).then(resp => {
				if (resp.data.success) {
					this.folders = resp.data.data.folders || []
					this.documents = resp.data.data.documents || []
					this.breadcrumb = resp.data.data.breadcrumb || []
					this.storage = resp.data.data.storage || { tenant: 0, client: 0, total: 0 }
				}
			}).catch(() => {}).finally(() => { this.loading = false })
		},
		navigateToFolder(folderId) {
			if (folderId) {
				this.$router.push({ name: 'portal-documents-folder', params: { folderId } })
			} else {
				this.$router.push({ name: 'portal-documents' })
			}
		},
		async loadVersions(docId) {
			try {
				var resp = await this.axios.get(this.apiBase + '/documents/' + docId + '/versions')
				if (resp.data.success) {
					this.docVersions = resp.data.data || []
				}
			} catch (e) {
				this.docVersions = []
			}
		},
		showSnackbar(text, color, timeout) {
			this.$store.commit('setSnackbar', { timeout: timeout || 6000, text, value: true, color: color || 'success' })
		},
		// Endpoint dedicato del portale (documents/upload); usato sia dall'upload di file
		// che dall'upload di cartelle orchestrato dal mixin (uploadFilesWithPaths).
		async uploadSingleFile(file, folderId) {
			var formData = new FormData()
			formData.append('file', file)
			if (folderId) formData.append('folder_id', folderId)
			await this.axios.post('portal/documents/upload', formData, {
				headers: { 'Content-Type': 'multipart/form-data' },
				silentValidation: true
			})
		},
		async handleVersionUpload(e) {
			var file = e.target.files[0]
			if (!file || !this.selectedDoc) return

			var formData = new FormData()
			formData.append('file', file)

			try {
				await this.axios.post('portal/documents/' + this.selectedDoc.id + '/new-version', formData, {
					headers: { 'Content-Type': 'multipart/form-data' }
				})
				this.loadContent()
				this.loadVersions(this.selectedDoc.id)
			} catch (e) {
				// errore
			}
			e.target.value = ''
		},
		canModify(item) {
			if (!item || item.area !== 'client' || item.is_shared) return false
			var role = this.$store.getters.portalRole
			if (role === 'manager') return true
			var user = this.$store.getters.portalUser
			return user && item.user_id === user.id
		},
		triggerFileInput() {
			if (this.$refs.fileInput) {
				this.$refs.fileInput.click()
			}
		},
		formatDateShort(date) {
			if (!date) return ''
			return new Date(date).toLocaleDateString('it-IT', {
				day: '2-digit', month: 'short', year: 'numeric'
			})
		}
	}
}
</script>

<style src="@/css/dms.css"></style>

<style scoped>
.marquee-rect {
	position: absolute;
	border: 1px dashed var(--v-primary-base, #1976d2);
	background: rgba(25, 118, 210, 0.08);
	pointer-events: none;
	z-index: 5;
}
.dms-doc-card.selected-multi,
.v-card.selected-multi {
	outline: 2px solid var(--v-primary-base, #1976d2);
	outline-offset: -2px;
}
.bulk-toolbar {
	position: sticky;
	top: 0;
	z-index: 4;
}
</style>
